import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import { ArrowLeft, ExternalLink, Newspaper, Languages } from "lucide-react";
import { steamMarkupToHtml } from "../lib/steamMarkup";

const fmtDate = (ts) =>
  ts ? new Date(ts * 1000).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "";

export default function NewsArticle() {
  const { id } = useParams();
  const { lang } = useLang();
  const [art, setArt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setArt(null);
    setError(null);
    api.get(`/news/${id}/full`)
      .then(({ data }) => setArt(data))
      .catch((err) => setError(err?.response?.status === 404 ? "not_found" : "generic"));
  }, [id]);

  // Alcune fonti (es. SteamDB) bloccano il hotlink delle immagini: se un'immagine non carica, la nascondiamo.
  // Doppio passaggio (immediato + dopo 3s) perché il blocco ORB del browser non sempre scatena l'evento error.
  useEffect(() => {
    if (!art) return;
    const sweep = () => {
      document.querySelectorAll(".news-body img").forEach((img) => {
        if (img.style.display === "none") return;
        if (img.complete && img.naturalWidth === 0) img.style.display = "none";
        else img.addEventListener("error", () => { img.style.display = "none"; }, { once: true });
      });
    };
    sweep();
    const t1 = setTimeout(sweep, 2500);
    const t2 = setTimeout(sweep, 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [art]);

  const isIt = lang === "it";
  const title = art ? (isIt && art.title_it ? art.title_it : art.title) : "";
  const body = art ? (isIt ? art.body_it : art.body_en) : "";

  return (
    <main className="pt-32 pb-28" data-testid="news-article-page">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Link to="/news" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-steel hover:text-wardogs transition-colors duration-300 mb-10" data-testid="news-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Tutte le news
        </Link>

        {error && (
          <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-10 text-center" data-testid="news-article-error">
            <Newspaper className="w-8 h-8 text-[#2A2A2A] mx-auto mb-4" />
            <p className="font-mono text-xs text-steel">
              {error === "not_found" ? "Questa notizia non è più disponibile nel feed ufficiale." : "La notizia non si carica ora. Riprova tra poco."}
            </p>
          </div>
        )}

        {!art && !error && (
          <div data-testid="news-article-loading">
            <div className="h-4 w-32 bg-[#1A1A1A] animate-pulse mb-6" />
            <div className="h-10 w-3/4 bg-[#1A1A1A] animate-pulse mb-10" />
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-3 w-full bg-[#141414] animate-pulse mb-3" />)}
          </div>
        )}

        {art && (
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className={`chamfer-sm px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] font-bold ${
                art.source === "ufficiale" ? "bg-wardogs/15 text-wardogs border border-wardogs/40" : "bg-[#1A1A1A] text-steel border border-[#2A2A2A]"
              }`} data-testid="news-article-source">
                {art.source_label}
              </span>
              <span className="font-mono text-[10px] text-[#4A4D51]" data-testid="news-article-date">{fmtDate(art.date)}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] mb-8" data-testid="news-article-title">{title}</h1>
            {isIt && (
              <p className="chamfer-sm border border-wardogs/30 bg-wardogs/5 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-wardogs mb-10 inline-flex items-center gap-2" data-testid="news-translated-note">
                <Languages className="w-3.5 h-3.5" /> Articolo completo tradotto in italiano
              </p>
            )}
            <article className="news-body" data-testid="news-article-body" dangerouslySetInnerHTML={{ __html: steamMarkupToHtml(body) }} />
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="news-original-box">
              <Newspaper className="w-5 h-5 text-steel shrink-0" />
              <p className="text-sm text-steel flex-1 leading-relaxed">Preferisci la versione originale in inglese sul sito ufficiale?</p>
              <a href={art.url} target="_blank" rel="noopener noreferrer" className="chamfer-sm border border-wardogs text-wardogs hover:bg-wardogs hover:text-[#050505] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-bold transition-colors duration-300 inline-flex items-center gap-2 shrink-0" data-testid="news-original-link">
                Vai all'originale EN <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
